import {
  LucideIcon,
  Scale,
  Laptop,
  Calculator,
  Factory,
  Briefcase,
} from 'lucide-react'

export type CustomerCategory = 'all' | 'professional-services' | 'legal' | 'technology' | 'financial' | 'manufacturing'

/** Brand tone used for the story's generated halftone tile. */
export type StoryTone = 'blue' | 'pink' | 'purple' | 'green' | 'black'

/** Office app the story's main documents are built in. */
export type StoryApp = 'Word' | 'Excel' | 'PowerPoint'

export interface Metric {
  label: string
  before: string
  after: string
  improvement: string
}

export interface CustomerStory {
  id: string
  company: string
  logo?: string
  industry: string
  industryIcon: LucideIcon
  category: CustomerCategory
  employees: number
  location: string
  headline: string
  challenge: string
  solution: string
  quote: string
  author: string
  authorTitle: string
  metrics: Metric[]
  solutions: string[]
  featured: boolean
  /** Team that owns the documents in the story (derived from the story text). */
  team: string
  /** Office app those documents are built in (derived from the story text). */
  officeApp: StoryApp
  /** The document type the story is about (derived from the story text). */
  documentType: string
  tone: StoryTone
}

export const categories: Array<{ value: CustomerCategory; label: string }> = [
  { value: 'all', label: 'All Industries' },
  { value: 'professional-services', label: 'Professional Services' },
  { value: 'legal', label: 'Legal Services' },
  { value: 'technology', label: 'Technology' },
  { value: 'financial', label: 'Financial Services' },
  { value: 'manufacturing', label: 'Manufacturing' },
]

export const customerStories: CustomerStory[] = [
  {
    id: 'greenfield-consulting',
    company: 'Greenfield Consulting',
    industry: 'Professional Services',
    industryIcon: Briefcase,
    category: 'professional-services',
    employees: 45,
    location: 'Boston, MA',
    headline: 'From 5-day proposals to same-day delivery',
    challenge: 'Greenfield Consulting was losing deals to competitors who could respond faster. Their proposal process involved multiple people, countless versions, and a 5-day average turnaround. Senior consultants spent more time on paperwork than client work.',
    solution: 'Templatean automated their entire proposal workflow. Templates pull client data directly from their CRM. AI generates first drafts in minutes. Approval workflows replaced email chains. Now the same proposal that took 5 days ships in hours.',
    quote: "We used to lose deals because we couldn't turn proposals around fast enough. Now we respond the same day—and our proposals look better than ever.",
    author: 'Sarah Mitchell',
    authorTitle: 'Operations Director',
    metrics: [
      { label: 'Proposal turnaround', before: '5 days', after: 'Same day', improvement: '95% faster' },
      { label: 'Win rate', before: '32%', after: '41%', improvement: '+9 points' },
      { label: 'Consultant time on proposals', before: '15 hrs/week', after: '3 hrs/week', improvement: '80% reduction' },
      { label: 'Pricing errors', before: '~12/quarter', after: '0', improvement: '100% eliminated' },
    ],
    solutions: ['AI Document Generation', 'Template Management', 'CRM Integration', 'Workflow Automation'],
    featured: true,
    team: 'Sales',
    officeApp: 'Word',
    documentType: 'Proposals',
    tone: 'blue',
  },
  {
    id: 'chen-associates',
    company: 'Chen & Associates Legal',
    industry: 'Legal Services',
    industryIcon: Scale,
    category: 'legal',
    employees: 12,
    location: 'San Francisco, CA',
    headline: 'Client documents that used to take hours now take minutes',
    challenge: 'As a small but growing law firm, Chen & Associates struggled with document consistency. Each attorney had their own templates, formatting varied wildly, and junior associates spent hours on document prep instead of legal research.',
    solution: 'Templatean standardized their entire document library. Every engagement letter, contract, and filing now uses approved templates. Clause libraries let attorneys assemble complex documents quickly. Built-in compliance checks catch issues before they become problems.',
    quote: "Document quality used to depend on which attorney worked on it. Now every document that leaves our firm looks like it came from a top-tier practice.",
    author: 'David Chen',
    authorTitle: 'Managing Partner',
    metrics: [
      { label: 'Document prep time', before: '4-6 hours', after: '30 minutes', improvement: '90% faster' },
      { label: 'Formatting inconsistencies', before: 'Frequent', after: 'Zero', improvement: '100% eliminated' },
      { label: 'Junior associate billable time', before: '62%', after: '78%', improvement: '+16 points' },
      { label: 'Client document complaints', before: '3-4/month', after: '0', improvement: '100% eliminated' },
    ],
    solutions: ['Template Management', 'Clause Libraries', 'Compliance Checks', 'Brand Consistency'],
    featured: false,
    team: 'Legal',
    officeApp: 'Word',
    documentType: 'Engagement letters',
    tone: 'purple',
  },
  {
    id: 'techstart-solutions',
    company: 'TechStart Solutions',
    industry: 'Technology',
    industryIcon: Laptop,
    category: 'technology',
    employees: 85,
    location: 'Austin, TX',
    headline: 'Brand consistency across a fast-growing team',
    challenge: 'TechStart doubled their headcount in 18 months. Marketing struggled to keep up with requests for branded materials. Sales created their own decks. Product built their own docs. The brand was everywhere—and nowhere.',
    solution: 'Templatean became the single source of truth for all brand assets. Self-service portals let any team create on-brand content. Template locks protect critical brand elements. Usage analytics show what\'s working and what\'s not.',
    quote: "Our brand consistency went from a constant headache to something I don't even think about anymore. It just works.",
    author: 'Elena Rodriguez',
    authorTitle: 'Marketing Manager',
    metrics: [
      { label: 'Brand compliance', before: '45%', after: '98%', improvement: '+53 points' },
      { label: 'Marketing time on asset requests', before: '20 hrs/week', after: '2 hrs/week', improvement: '90% reduction' },
      { label: 'Logo versions in circulation', before: '12', after: '1', improvement: 'Single source' },
      { label: 'Time to create sales deck', before: '3 hours', after: '20 minutes', improvement: '89% faster' },
    ],
    solutions: ['Brand Asset Management', 'Self-Service Portals', 'Template Locks', 'Usage Analytics'],
    featured: true,
    team: 'Marketing',
    officeApp: 'PowerPoint',
    documentType: 'Sales decks',
    tone: 'pink',
  },
  {
    id: 'summit-advisory',
    company: 'Summit Advisory Group',
    industry: 'Financial Services',
    industryIcon: Calculator,
    category: 'financial',
    employees: 50,
    location: 'Chicago, IL',
    headline: 'Financial models the whole team can trust',
    challenge: 'Summit\'s financial analysts spent more time checking each other\'s work than building new models. Complex Excel files had hidden errors. Documentation was sparse. Auditors always had questions nobody could answer.',
    solution: 'Templatean\'s Excel Intelligence transformed how Summit builds models. AI catches errors in real-time. Automatic documentation explains every formula. Complete audit trails answer any question instantly.',
    quote: "The financial models our team builds now are more accurate and take half the time. I sleep better knowing the numbers are right.",
    author: 'David Nguyen',
    authorTitle: 'CFO',
    metrics: [
      { label: 'Model build time', before: '3-4 days', after: '4-6 hours', improvement: '80% faster' },
      { label: 'Formula errors found post-delivery', before: '~8/model', after: '<1/model', improvement: '95% reduction' },
      { label: 'Audit prep time', before: '3 weeks', after: '3 days', improvement: '85% faster' },
      { label: 'Time to explain model logic', before: '2-3 hours', after: '10 minutes', improvement: '95% faster' },
    ],
    solutions: ['Excel Intelligence', 'AI Formula Assistant', 'Model Documentation', 'Audit Trails'],
    featured: true,
    team: 'Finance',
    officeApp: 'Excel',
    documentType: 'Financial models',
    tone: 'green',
  },
  {
    id: 'pacific-manufacturing',
    company: 'Pacific Manufacturing Co.',
    industry: 'Manufacturing',
    industryIcon: Factory,
    category: 'manufacturing',
    employees: 200,
    location: 'Portland, OR',
    headline: 'HR documents that scale with growth',
    challenge: 'Pacific Manufacturing was expanding rapidly, adding new facilities and hiring hundreds of workers. HR was drowning in paperwork—offer letters, policy updates, compliance documents. Everything was manual, and mistakes were common.',
    solution: 'Templatean automated Pacific\'s entire HR document workflow. Offer letters generate from HRIS data. Policy updates distribute automatically with tracking. Compliance documentation is always audit-ready.',
    quote: "We went from a two-person team barely keeping up to handling triple the volume with the same headcount. Document automation changed everything.",
    author: 'Michael Chen',
    authorTitle: 'Director of Operations',
    metrics: [
      { label: 'Onboarding document time', before: '4 hours/hire', after: '15 minutes/hire', improvement: '94% faster' },
      { label: 'Policy acknowledgment rate', before: '67%', after: '99%', improvement: '+32 points' },
      { label: 'Compliance audit findings', before: '8-12', after: '0', improvement: 'Zero findings' },
      { label: 'HR headcount for docs', before: '2 FTE', after: '0.5 FTE', improvement: '75% reduction' },
    ],
    solutions: ['HR Document Generation', 'Policy Management', 'HRIS Integration', 'Compliance Documentation'],
    featured: false,
    team: 'HR',
    officeApp: 'Word',
    documentType: 'Offer letters',
    tone: 'black',
  },
]

export const featuredStories = customerStories.filter(story => story.featured)
export const storiesByCategory = (category: CustomerCategory): CustomerStory[] => {
  if (category === 'all') return customerStories
  return customerStories.filter(story => story.category === category)
}
